import { useState, useMemo, useRef, useEffect } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/24/outline';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
  isAfter,
  parseISO,
} from 'date-fns';
import { cn } from '@/utils/cn';

interface DatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  min?: string; // YYYY-MM-DD
  max?: string; // YYYY-MM-DD
  label?: string;
  className?: string;
  compact?: boolean;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function DatePicker({ value, onChange, min, max, label, className, compact }: DatePickerProps) {
  const selected = value ? parseISO(value) : null;
  const [viewMonth, setViewMonth] = useState(() => selected ?? new Date());

  // Reset viewMonth when value changes externally
  useEffect(() => {
    if (selected) setViewMonth(selected);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const minDate = min ? parseISO(min) : undefined;
  const maxDate = max ? parseISO(max) : undefined;

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [viewMonth]);

  const isDisabled = (day: Date) => {
    if (minDate && isBefore(day, minDate)) return true;
    if (maxDate && isAfter(day, maxDate)) return true;
    return false;
  };

  const handleSelect = (day: Date, close: () => void) => {
    if (isDisabled(day)) return;
    onChange(format(day, 'yyyy-MM-dd'));
    close();
  };

  const displayValue = selected ? format(selected, 'dd/MM/yyyy') : '';

  return (
    <div className={cn('relative', className)}>
      {label && (
        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">{label}</label>
      )}
      <Popover>
        {({ close }) => (
          <>
            <PopoverButton
              className={cn(
                'inline-flex w-full items-center gap-2 rounded-lg border bg-white text-left text-sm transition-colors',
                'border-gray-300 text-gray-700 hover:border-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-500',
                compact ? 'px-2 py-1.5' : 'px-3 py-2',
              )}
            >
              <CalendarIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <span className={cn('flex-1', !displayValue && 'text-gray-400 dark:text-gray-500')}>
                {displayValue || 'dd/mm/yyyy'}
              </span>
            </PopoverButton>

            <PopoverPanel
              anchor="bottom start"
              className="z-50 mt-1 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-gray-800"
            >
              {/* Month navigation */}
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setViewMonth((m) => subMonths(m, 1))}
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {format(viewMonth, 'MMMM yyyy')}
                </span>
                <button
                  type="button"
                  onClick={() => setViewMonth((m) => addMonths(m, 1))}
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Weekday headers */}
              <div className="mb-1 grid grid-cols-7 text-center">
                {WEEKDAYS.map((d) => (
                  <span key={d} className="py-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {d}
                  </span>
                ))}
              </div>

              {/* Days grid */}
              <div className="grid grid-cols-7">
                {days.map((day) => {
                  const isSelected = selected && isSameDay(day, selected);
                  const isCurrentMonth = isSameMonth(day, viewMonth);
                  const disabled = isDisabled(day);
                  const today = isToday(day);

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleSelect(day, close)}
                      className={cn(
                        'mx-auto flex h-8 w-8 items-center justify-center rounded-lg text-sm transition-colors',
                        isSelected
                          ? 'bg-brand-600 font-semibold text-white'
                          : today && isCurrentMonth
                            ? 'font-semibold text-brand-600 ring-1 ring-brand-300 dark:text-brand-400 dark:ring-brand-700'
                            : isCurrentMonth
                              ? 'text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700'
                              : 'text-gray-300 hover:bg-gray-50 dark:text-gray-600 dark:hover:bg-gray-700/50',
                        disabled && 'cursor-not-allowed opacity-30 hover:bg-transparent dark:hover:bg-transparent',
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              {/* Today shortcut */}
              <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    const today = new Date();
                    if (!isDisabled(today)) {
                      onChange(format(today, 'yyyy-MM-dd'));
                      close();
                    }
                  }}
                  className="w-full rounded-lg py-1 text-center text-xs font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20"
                >
                  Today
                </button>
              </div>
            </PopoverPanel>
          </>
        )}
      </Popover>
    </div>
  );
}
